import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Observable } from 'rxjs';
import { get } from 'lodash-es';
import { JwtHelperService } from '@auth0/angular-jwt';

import { Credential, LoginHistory, User, UserToken } from '@model/authentication.model';
import { EncrDecrService } from './encr-decr.service';
import { environment } from 'src/environments/environment';

const jwtHelperService: JwtHelperService = new JwtHelperService();

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  currentUser: User;
  private currentUserSubject$: Subject<User> = new Subject<User>();
  currentUser$: Observable<User> = this.currentUserSubject$.asObservable();

  private readonly credentialKey: string = 'credential';
  private readonly language: string = 'lang';

  constructor(private http: HttpClient, private translate: TranslateService, private encrDecrService: EncrDecrService) {
    this.setCurrentCredential();
  }

  setCurrentCredential(credential?: Credential): void {
    if (credential) {
      const encrCredential: string = this.encrDecrService.enCryption(JSON.stringify(credential));
      localStorage.setItem(this.credentialKey, encrCredential);
      localStorage.setItem(this.language, this.translate.currentLang);
      this.setUserByCredential(credential);
    } else if (this.getCredential()) {
      const credentialFromStorage: Credential = this.getCredential();
      this.setUserByCredential(credentialFromStorage);
    } else {
      this.clearCurrentUser();
    }
  }

  getLanguage(): string {
    return localStorage.getItem(this.language);
  }

  getReferenceId(): string | null {
    const credential: Credential = this.getCredential();
    return credential ? credential.referenceId : null;
  }

  getIdentityType(): string | null {
    const credential: Credential = this.getCredential();
    return credential ? credential.identityType : null;
  }

  getIdentityNo(): string | null {
    const credential: Credential = this.getCredential();
    return credential ? credential.identityNo : null;
  }

  getUserType(): string {
    if (!this.currentUser) {
      return null;
    }
    return this.currentUser.userType;
  }

  getUserBranchCode(): string {
    if (!this.currentUser) {
      return null;
    }
    return this.currentUser.branchCode;
  }

  getUserIMDCode(): string {
    if (!this.currentUser) {
      return null;
    }
    return this.currentUser.intermediaryId;
  }

  sendUserMessage(user: User): Promise<void> {
    return new Promise(
      (resolve: any, reject: any): void => {
        try {
          this.currentUser = user;
          this.currentUserSubject$.next(user);

          if (user) {
            resolve();
          } else {
            reject();
          }
        } catch (error) {
          reject();
        }
      }
    );
  }

  clearCurrentUser(): void {
    this.currentUser = null;
    localStorage.clear();
  }

  getHttpHeader(contentType: string = 'application/json'): HttpHeaders {
    const referenceId: string = this.getReferenceId();
    const lang: string = this.getLanguage();

    if (!this.currentUser) {
      return null;
    }

    const commonHeader: any = {
      'Accept-Language': lang || this.translate.currentLang,
      Authorization: `Bearer ${get(this.currentUser, 'accessToken.access_token', '')}`,
      'Content-Type': contentType
    };

    if (referenceId) {
      return new HttpHeaders({
        ...commonHeader,
        referenceId
      });
    }
    return new HttpHeaders(commonHeader);
  }

  getLoginHistory(): Observable<LoginHistory> {
    const idNo: string = this.getIdentityNo();
    const idType: string = this.getIdentityType();
    const url = `${environment.endpoint.api}/login/history/${idType}?identityNo=${idNo}`;

    return this.http.get<LoginHistory>(url);
  }

  checkExpiredToken(): void {
    if (!this.currentUser || !this.currentUser.accessToken || !this.currentUser.accessToken.access_token) {
      return;
    }

    if (jwtHelperService.isTokenExpired(this.currentUser.accessToken.access_token)) {
      window.parent.postMessage([{ channel: 'Unauthorized' }], '*');
    }
  }

  private setUserByCredential(credential: Credential): void {
    const userToken: UserToken = { access_token: credential.accessToken } as UserToken;
    const user: User = { accessToken: userToken } as User;

    this.currentUser = user;
  }

  private getCredential(): Credential {
    const credentialKey: string = localStorage.getItem(this.credentialKey);
    return credentialKey ? JSON.parse(this.encrDecrService.deCryption(credentialKey)) : null;
  }
}
